/**
 * 旧 id → 新 id 的别名表(ADR-053):机型号与真实机场代码不再出现在 URL 与用户看得到的地方,但旧链接、旧回放、
 * 旧的本机进度与账号里的旧记录都要照样能用。**所有别名只在这一个文件里**;模拟器入口(改写地址栏)、网站进度、
 * API(课程 id、上课记录、排行榜、回放校验)、D1 迁移 `0009_neutral_ids.sql` 都按这张表。
 *
 * - 课程:`c172-*` → `trainer-*`,`b737-*` → `airliner-*`(后半截不变)。
 * - 自由飞行的机型参数:`aircraft=737` → `aircraft=airliner`,`aircraft=172` → `aircraft=trainer`(不给 = 教练机)。
 * - 机场参数:`airport=cykf` → `airport=foxfield`,`airport=cytz` → `airport=harbourpoint`。机场泡泡的内部 id
 *   (`@aerofox/world` 的 `AirportId`:cykf / cytz)与数据文件名不变,URL 用的名字在 `AIRPORT_BUBBLES[id].param`;
 *   apps/sim 的测试核对这张表与泡泡一一对应。
 *
 * 只有常量与纯函数,不带 zod,模拟器可以按子路径 `@aerofox/shared/legacy-ids` 引入。
 */

/** 旧课程 id → 新课程 id。 */
export const LEGACY_LESSON_IDS: Readonly<Record<string, string>> = {
  "c172-straight-level": "trainer-straight-level",
  "c172-turns": "trainer-turns",
  "c172-climb-descent": "trainer-climb-descent",
  "c172-slow-flight": "trainer-slow-flight",
  "c172-takeoff": "trainer-takeoff",
  "c172-pattern": "trainer-pattern",
  "c172-landing": "trainer-landing",
  "c172-crosswind-landing": "trainer-crosswind-landing",
  "c172-go-around": "trainer-go-around",
  "c172-checkride": "trainer-checkride",
  "b737-fma": "airliner-fma",
  "b737-ils": "airliner-ils",
  "b737-go-around": "airliner-go-around",
};

/** 自由飞行 `?aircraft=` 的旧值 → 新值。 */
export const LEGACY_AIRCRAFT_PARAMS = { "737": "airliner", "172": "trainer" } as const satisfies Readonly<Record<string, string>>;

/**
 * `?airport=` 的旧值(真实 ICAO 代码,小写)→ 新值(虚构机场名)。网站也用它把机场泡泡的内部 id 换成 URL 名,所以新加的泡泡也登记在这里:
 * 灯笼谷(xlnv,ADR-107)、玉门山(xjdg,ADR-108)、云原(xclm,ADR-109)与星光湾(xslb,ADR-111)的内部 id 本身就是虚构代码,`?airport=xlnv` / `xjdg` / `xclm` / `xslb` 一样换成 `lanternvalley` / `jadegate` / `cloudmeadow` / `starlightbay`。
 */
export const LEGACY_AIRPORT_PARAMS = { cykf: "foxfield", cytz: "harbourpoint", xlnv: "lanternvalley", xjdg: "jadegate", xclm: "cloudmeadow", xslb: "starlightbay", xlmp: "lumenport", xsrf: "sunreef" } as const satisfies Readonly<Record<string, string>>;

/** 课程 id 的规范形式:旧 id 换成新 id,其余原样(不认识的 id 由调用方照旧拒收)。 */
export function canonicalLessonId(id: string): string {
  return Object.hasOwn(LEGACY_LESSON_IDS, id) ? LEGACY_LESSON_IDS[id]! : id;
}

/**
 * 模拟器地址的查询串换成规范形式:`lesson` / `aircraft` / `airport` 的旧值换成新值,其余参数与顺序不动。
 * 有改动返回新的查询串(带 `?`),没有改动返回 null(调用方据此决定要不要 `history.replaceState`)。
 */
export function canonicalSimSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  let changed = false;
  const swap = (key: string, table: Readonly<Record<string, string>>, fold: boolean) => {
    const raw = params.get(key);
    if (raw === null) return;
    const v = fold ? raw.trim().toLowerCase() : raw;
    if (Object.hasOwn(table, v)) {
      params.set(key, table[v]!);
      changed = true;
    }
  };
  swap("lesson", LEGACY_LESSON_IDS, false);
  swap("aircraft", LEGACY_AIRCRAFT_PARAMS, true);
  swap("airport", LEGACY_AIRPORT_PARAMS, true);
  return changed ? `?${params.toString()}` : null;
}
